import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSettingsStore } from '@/stores';

interface UserBehavior {
  preferredFeatures: string[];
  usagePatterns: {
    chatFrequency: number;
    editorUsage: number;
    searchQueries: string[];
    timeSpent: { [feature: string]: number };
  };
  uiPreferences: {
    theme: 'light' | 'dark' | 'auto';
    layout: 'compact' | 'comfortable' | 'spacious' | 'standard' | 'personalized';
    shortcuts: { [action: string]: string };
  };
  personalization: {
    suggestedFeatures: string[];
    hiddenElements: string[];
    prioritizedActions: string[];
  };
}

interface PersonalizationContextType {
  behavior: UserBehavior;
  updateBehavior: (updates: Partial<UserBehavior>) => void;
  getPersonalizedSuggestions: () => string[];
  adaptInterface: (elementId: string) => { visible: boolean; priority: number };
  trackAction: (action: string, metadata?: any) => void;
}

const PersonalizationContext = createContext<PersonalizationContextType | null>(null);

const defaultBehavior: UserBehavior = {
  preferredFeatures: [],
  usagePatterns: {
    chatFrequency: 0,
    editorUsage: 0,
    searchQueries: [],
    timeSpent: {},
  },
  uiPreferences: {
    theme: 'auto',
    layout: 'comfortable',
    shortcuts: {},
  },
  personalization: {
    suggestedFeatures: [],
    hiddenElements: [],
    prioritizedActions: [],
  },
};

export const PersonalizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { settings } = useSettingsStore();
  const [behavior, setBehavior] = useState<UserBehavior>(() => {
    const stored = localStorage.getItem('user-behavior');
    return stored ? { ...defaultBehavior, ...JSON.parse(stored) } : defaultBehavior;
  });

  // Persist behavior data
  useEffect(() => {
    localStorage.setItem('user-behavior', JSON.stringify(behavior));
  }, [behavior]);

  // Learn from user interactions
  useEffect(() => {
    // Track theme preference
    if (settings.theme && settings.theme !== behavior.uiPreferences.theme) {
      updateBehavior({
        uiPreferences: { ...behavior.uiPreferences, theme: settings.theme as any },
      });
    }

    // Analyze usage patterns periodically
    const interval = setInterval(() => {
      analyzeUsagePatterns();
    }, 300000); // Every 5 minutes

    return () => clearInterval(interval);
  }, [settings.theme]);

  const updateBehavior = (updates: Partial<UserBehavior>) => {
    setBehavior(prev => ({ ...prev, ...updates }));
  };

  const analyzeUsagePatterns = () => {
    // Simple pattern analysis - in production, this would use ML
    const now = Date.now();
    const recentActivity = Object.entries(behavior.usagePatterns.timeSpent)
      .filter(([_, time]) => time > 0)
      .sort(([, a], [, b]) => b - a);

    if (recentActivity.length > 0) {
      const mostUsed = recentActivity[0][0];
      const suggestions = [];

      // Generate suggestions based on usage
      if (mostUsed === 'chat' && !behavior.personalization.suggestedFeatures.includes('advanced-chat')) {
        suggestions.push('advanced-chat');
      }
      if (mostUsed === 'editor' && !behavior.personalization.suggestedFeatures.includes('code-templates')) {
        suggestions.push('code-templates');
      }

      if (suggestions.length > 0) {
        updateBehavior({
          personalization: {
            ...behavior.personalization,
            suggestedFeatures: [...behavior.personalization.suggestedFeatures, ...suggestions],
          },
        });
      }
    }
  };

  const getPersonalizedSuggestions = (): string[] => {
    const suggestions = [...behavior.personalization.suggestedFeatures];

    // Add contextual suggestions based on current usage
    if (behavior.usagePatterns.chatFrequency > 10) {
      suggestions.push('conversation-templates');
    }
    if (behavior.usagePatterns.editorUsage > 5) {
      suggestions.push('code-snippets');
    }

    return [...new Set(suggestions)]; // Remove duplicates
  };

  const adaptInterface = (elementId: string): { visible: boolean; priority: number } => {
    const isHidden = behavior.personalization.hiddenElements.includes(elementId);

    // Calculate priority based on usage
    let priority = 1;
    if (behavior.usagePatterns.timeSpent[elementId]) {
      priority = Math.min(behavior.usagePatterns.timeSpent[elementId] / 1000, 5); // Max priority 5
    }

    return {
      visible: !isHidden,
      priority: Math.round(priority),
    };
  };

  const trackAction = (action: string, metadata?: any) => {
    const now = Date.now();

    // Update usage patterns
    const updatedPatterns = { ...behavior.usagePatterns };

    if (action === 'chat-message') {
      updatedPatterns.chatFrequency += 1;
    } else if (action === 'editor-change') {
      updatedPatterns.editorUsage += 1;
    } else if (action === 'search-query' && metadata?.query) {
      updatedPatterns.searchQueries = [
        metadata.query,
        ...updatedPatterns.searchQueries.slice(0, 9), // Keep last 10
      ];
    }

    // Track time spent on features
    updatedPatterns.timeSpent[action] = (updatedPatterns.timeSpent[action] || 0) + 1;

    // Update preferred features based on usage
    const preferredFeatures = [...behavior.preferredFeatures];
    if (!preferredFeatures.includes(action) && updatedPatterns.timeSpent[action] > 5) {
      preferredFeatures.push(action);
    }

    updateBehavior({
      usagePatterns: updatedPatterns,
      preferredFeatures: [...new Set(preferredFeatures)],
    });
  };

  const contextValue: PersonalizationContextType = {
    behavior,
    updateBehavior,
    getPersonalizedSuggestions,
    adaptInterface,
    trackAction,
  };

  return (
    <PersonalizationContext.Provider value={contextValue}>
      {children}
    </PersonalizationContext.Provider>
  );
};

export const usePersonalization = () => {
  const context = useContext(PersonalizationContext);
  if (!context) {
    throw new Error('usePersonalization must be used within a PersonalizationProvider');
  }
  return context;
};